import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { Warning, SignOut } from "@phosphor-icons/react";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/api";

/**
 * Sticky banner shown when the current session is an impersonation issued by a
 * super admin. Clicking "Exit" returns to the original super admin session.
 */
export default function ImpersonationBanner() {
  const { user, stopImpersonating } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);

  if (!user || !user.impersonator_id) return null;

  const onExit = async () => {
    setBusy(true);
    try {
      await stopImpersonating();
      toast.success("Returned to your account");
      navigate("/admin");
    } catch (e) {
      const m = apiErrorMessage(e);
      if (m) toast.error(m);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="sticky top-0 z-[60] w-full bg-[#D92D20] text-white shadow-md"
      data-testid="impersonation-banner"
    >
      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-2 flex items-center gap-3">
        <Warning size={18} weight="bold" />
        <div className="flex-1 text-sm leading-tight">
          <span className="font-semibold">Viewing as {user.name || user.email}</span>
          <span className="ml-2 text-white/80 text-xs hidden sm:inline">
            ({user.role}{user.is_super_admin ? " · super" : ""})
          </span>
        </div>
        <button
          type="button"
          onClick={onExit}
          disabled={busy}
          className="inline-flex items-center gap-1.5 bg-white/15 hover:bg-white/25 disabled:opacity-60 transition-colors rounded-md px-3 py-1 text-xs font-semibold"
          data-testid="impersonation-exit-button"
        >
          <SignOut size={14} weight="bold" />
          {busy ? "Returning…" : "Exit impersonation"}
        </button>
      </div>
    </div>
  );
}
